import { useEffect, useState, type FormEvent } from "react";
import { asksForServerUrl, getServerUrl, needsServerUrl, setServerUrl } from "@/api/base";
import { login } from "@/api/client";
import { clearIdleSignOutNotice, idleSignOutNotice } from "@/lib/idle-session";
import "./LoginPage.css";

/**
 * Director sign-in, in the PeliBill design: the pelican scene and what the app does on the
 * left, the form on the right (stacked on a phone). Credentials are typed here and exchanged
 * for tokens by `login()`.
 *
 * The Android and Windows apps are not served by the server, so they also ask where it is.
 * The address is remembered on the device and can be corrected here after signing out.
 */
export function LoginPage({ onLogin }: { onLogin: () => void }) {
  const asksForServer = asksForServerUrl();
  const [server, setServer] = useState(() => getServerUrl() ?? "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Nothing stored yet, so start the director in the field they have to fill in first.
  const [startOnServer] = useState(needsServerUrl);
  // Shown once: dropped from storage on reading, so a reload does not repeat it.
  const [idleNotice] = useState(idleSignOutNotice);
  useEffect(clearIdleSignOutNotice, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Save the address first: a malformed one is reported before any request is made.
      if (asksForServer) setServerUrl(server);
      await login(username, password);
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <section className="hero" aria-labelledby="pitch-title">
        <img src="/pelican-accounts.svg" alt="" aria-hidden="true" />
        <div className="suite">
          <span className="product">
            Peli<b>Bill</b>
          </span>
          <span className="modules">Pelican Blue Ocean accounts</span>
        </div>
        <div className="pitch">
          <h1 id="pitch-title">Every invoice and every payment, in one place</h1>
          <p>
            Raise invoices for each job and see at a glance what has been paid and what is
            still due.
          </p>
        </div>
        <ul className="products">
          <li>
            <h2>Invoices</h2>
            <p>
              Create invoices for stowage planning, PeliStow and freight jobs, and keep every
              one on record for each client.
            </p>
          </li>
          <li>
            <h2>Payments</h2>
            <p>Record payments as they come in and see what is outstanding, client by client.</p>
          </li>
        </ul>
      </section>

      <main className="login">
        <div className="lockup">
          <img src="/pb-logo.png" alt="" width={64} height={48} />
          <div className="lockup-text">
            <strong>PELICAN BLUE OCEAN</strong>
            <span>LOGISTICS &amp; SOLUTIONS</span>
          </div>
        </div>

        <h2>Sign in to PeliBill</h2>
        <p className="lead">Directors sign in with the username and password they were given.</p>
        {idleNotice && (
          <p role="status" className="notice">
            {idleNotice}
          </p>
        )}

        <form onSubmit={(event) => void onSubmit(event)}>
          {asksForServer && (
            <>
              <label htmlFor="server">Server address</label>
              <input
                id="server"
                type="url"
                inputMode="url"
                autoComplete="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="https://accounts.pelicanblueocean.com"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                required
                autoFocus={startOnServer}
                aria-describedby="server-hint"
              />
              <span id="server-hint" className="hint">
                The same address you would open in a browser. Ask a fellow director if you are
                unsure.
              </span>
            </>
          )}

          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus={!startOnServer}
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}

          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="help">
          Need access? Email{" "}
          <a href="mailto:info@pelicanblueocean.com">info@pelicanblueocean.com</a>
        </p>
      </main>
    </div>
  );
}
